import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { toast } from 'sonner';
import { SlaBadge } from '../SlaTimer';
import {
  RefreshCw, ShieldAlert, Timer, CheckCircle2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

export default function SlaMonitorView() {
  const navigate = useNavigate();
  const [slaData, setSlaData] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [slaView, setSlaView] = useState('monitor');
  const [analyticsDays, setAnalyticsDays] = useState('30');

  useEffect(() => {
    (async () => {
      try {
        const [dashRes, analyticsRes] = await Promise.all([
          api.get('/api/sla/dashboard'),
          api.get(`/api/sla/analytics?days=${analyticsDays}`),
        ]);
        setSlaData(dashRes.data);
        setAnalytics(analyticsRes.data);
      } catch { toast.error('Failed to load SLA data'); }
      finally { setLoading(false); }
    })();
  }, [analyticsDays]);

  if (loading) return <div className="flex items-center justify-center py-20"><RefreshCw className="w-5 h-5 animate-spin text-gray-400" /></div>;

  const TicketRow = ({ t }) => (
    <button
      onClick={() => navigate(`/tickets/${t.id}`)}
      className="w-full flex items-center justify-between p-3 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 text-left transition-colors"
      data-testid={`sla-ticket-${t.id}`}
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-900 truncate">{t.title}</p>
        <div className="flex items-center gap-2 mt-1">
          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded badge-${t.priority}`}>{t.priority}</span>
          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded badge-${t.status}`}>{t.status?.replace('_', ' ')}</span>
          <span className="text-[10px] text-gray-400">{t.assignee_name || 'Unassigned'}</span>
        </div>
      </div>
      {t.sla_status && (
        <div className="text-right shrink-0 ml-3">
          <SlaBadge slaStatus={t.sla_status} />
          {t.sla_status.remaining_seconds > 0 && (
            <p className="text-[10px] text-gray-400 mt-0.5 font-mono">
              {Math.floor(t.sla_status.remaining_seconds / 3600)}h {Math.floor((t.sla_status.remaining_seconds % 3600) / 60)}m left
            </p>
          )}
        </div>
      )}
    </button>
  );

  const fmtMin = (m) => m >= 60 ? `${(m / 60).toFixed(1)}h` : `${Math.round(m)}m`;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="sla-monitor">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900">SLA Monitor</h1>
          <p className="text-sm text-gray-500 mt-1">Real-time SLA status and historical analytics</p>
        </div>
      </div>

      <div className="flex gap-1 border-b border-gray-200 pb-0">
        {[{k: 'monitor', l: 'Live Monitor'}, {k: 'analytics', l: 'Analytics'}].map(tab => (
          <button key={tab.k} onClick={() => setSlaView(tab.k)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${slaView === tab.k ? 'border-[#D4A843] text-[#D4A843]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            data-testid={`sla-tab-${tab.k}`}
          >{tab.l}</button>
        ))}
      </div>

      {slaView === 'monitor' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <p className="text-xs text-gray-500 mb-1">Total Open</p>
              <p className="font-heading text-2xl font-bold text-gray-900">{slaData?.total_open || 0}</p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-5">
              <div className="flex items-center gap-2 mb-1"><ShieldAlert className="w-4 h-4 text-red-600" strokeWidth={1.5} /><p className="text-xs text-red-600 font-semibold">Breached</p></div>
              <p className="font-heading text-2xl font-bold text-red-700">{slaData?.breach_count || 0}</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-5">
              <div className="flex items-center gap-2 mb-1"><Timer className="w-4 h-4 text-amber-600" strokeWidth={1.5} /><p className="text-xs text-amber-600 font-semibold">At Risk</p></div>
              <p className="font-heading text-2xl font-bold text-amber-700">{slaData?.at_risk_count || 0}</p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-5">
              <div className="flex items-center gap-2 mb-1"><CheckCircle2 className="w-4 h-4 text-emerald-600" strokeWidth={1.5} /><p className="text-xs text-emerald-600 font-semibold">On Track</p></div>
              <p className="font-heading text-2xl font-bold text-emerald-700">{(slaData?.on_track || []).length}</p>
            </div>
          </div>
          {(slaData?.breached || []).length > 0 && (
            <div><h2 className="text-sm font-bold text-red-600 uppercase tracking-wider mb-3 flex items-center gap-2"><ShieldAlert className="w-4 h-4" /> SLA Breached ({slaData.breached.length})</h2>
              <div className="space-y-2">{slaData.breached.map(t => <TicketRow key={t.id} t={t} />)}</div></div>
          )}
          {(slaData?.at_risk || []).length > 0 && (
            <div><h2 className="text-sm font-bold text-amber-600 uppercase tracking-wider mb-3 flex items-center gap-2"><Timer className="w-4 h-4" /> At Risk ({slaData.at_risk.length})</h2>
              <div className="space-y-2">{slaData.at_risk.map(t => <TicketRow key={t.id} t={t} />)}</div></div>
          )}
          {(slaData?.on_track || []).length > 0 && (
            <div><h2 className="text-sm font-bold text-emerald-600 uppercase tracking-wider mb-3 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> On Track ({slaData.on_track.length})</h2>
              <div className="space-y-2">{slaData.on_track.map(t => <TicketRow key={t.id} t={t} />)}</div></div>
          )}
          {slaData?.total_open === 0 && <p className="text-center text-gray-400 py-8">No open tickets to monitor</p>}
        </>
      )}

      {slaView === 'analytics' && analytics && (
        <div className="space-y-6" data-testid="sla-analytics">
          <div className="flex items-center gap-3">
            <Select value={analyticsDays} onValueChange={v => setAnalyticsDays(v)}>
              <SelectTrigger className="w-36 rounded-lg" data-testid="analytics-period-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/export/sla-analytics?days=${analyticsDays}`} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm" className="text-xs rounded-lg" data-testid="export-sla-btn">Export CSV</Button>
            </a>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Total Tickets</p>
              <p className="font-heading text-2xl font-bold text-gray-900 mt-1">{analytics.total_tickets}</p>
            </div>
            <div className={`rounded-lg p-5 ${analytics.compliance_pct >= 90 ? 'bg-emerald-50 border border-emerald-200' : analytics.compliance_pct >= 70 ? 'bg-amber-50 border border-amber-200' : 'bg-red-50 border border-red-200'}`}>
              <p className="text-xs text-gray-600 uppercase tracking-wider">SLA Compliance</p>
              <p className={`font-heading text-2xl font-bold mt-1 ${analytics.compliance_pct >= 90 ? 'text-emerald-700' : analytics.compliance_pct >= 70 ? 'text-amber-700' : 'text-red-700'}`}>{analytics.compliance_pct}%</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Avg Resolution</p>
              <p className="font-heading text-2xl font-bold text-gray-900 mt-1">{fmtMin(analytics.avg_resolution_min)}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Median</p>
              <p className="font-heading text-2xl font-bold text-gray-900 mt-1">{fmtMin(analytics.median_resolution_min)}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <p className="text-xs text-gray-500 uppercase tracking-wider">P95</p>
              <p className="font-heading text-2xl font-bold text-gray-900 mt-1">{fmtMin(analytics.p95_resolution_min)}</p>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4">SLA by Priority</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="sla-priority-table">
                <thead><tr className="border-b bg-gray-50">
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Priority</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Total</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Resolved</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Breached</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Compliance</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Avg Time</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">SLA Target</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {['critical', 'high', 'medium', 'low'].map(p => {
                    const s = analytics.priority_stats?.[p] || {};
                    return (
                      <tr key={p} data-testid={`sla-priority-${p}`}>
                        <td className="px-4 py-3"><span className={`text-xs font-semibold px-2 py-0.5 rounded badge-${p}`}>{p}</span></td>
                        <td className="px-4 py-3 font-medium">{s.total || 0}</td>
                        <td className="px-4 py-3">{s.resolved || 0}</td>
                        <td className="px-4 py-3 text-red-600 font-semibold">{s.breached || 0}</td>
                        <td className="px-4 py-3"><span className={`text-xs font-bold ${(s.compliance_pct || 0) >= 90 ? 'text-emerald-600' : (s.compliance_pct || 0) >= 70 ? 'text-amber-600' : 'text-red-600'}`}>{s.compliance_pct || 0}%</span></td>
                        <td className="px-4 py-3 font-mono text-xs">{fmtMin(s.avg_resolution_min || 0)}</td>
                        <td className="px-4 py-3 font-mono text-xs text-gray-400">{fmtMin(s.sla_target_min || 0)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {analytics.daily_trend?.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4">Daily Ticket Trend</h3>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {analytics.daily_trend.map(d => (
                  <div key={d.date} className="flex items-center gap-3 p-2 rounded bg-gray-50" data-testid={`trend-${d.date}`}>
                    <span className="text-xs font-mono text-gray-500 w-20">{d.date}</span>
                    <div className="flex-1 flex items-center gap-2">
                      <div className="flex items-center gap-1"><span className="text-[10px] text-gray-500">Created:</span><span className="text-xs font-bold text-gray-700">{d.created}</span></div>
                      <div className="flex items-center gap-1"><span className="text-[10px] text-emerald-500">Resolved:</span><span className="text-xs font-bold text-emerald-600">{d.resolved}</span></div>
                      {d.breached > 0 && <div className="flex items-center gap-1"><span className="text-[10px] text-red-500">Breached:</span><span className="text-xs font-bold text-red-600">{d.breached}</span></div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {Object.keys(analytics.category_stats || {}).length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4">By Category</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {Object.entries(analytics.category_stats).map(([cat, s]) => (
                  <div key={cat} className="p-3 rounded-lg bg-gray-50" data-testid={`cat-${cat}`}>
                    <p className="text-xs font-semibold text-gray-900 capitalize">{cat.replace(/_/g, ' ')}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-xs text-gray-500">{s.total} total</span>
                      <span className="text-xs text-emerald-600">{s.resolved} resolved</span>
                      {s.breached > 0 && <span className="text-xs text-red-600">{s.breached} breached</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {analytics.total_tickets === 0 && <p className="text-center text-gray-400 py-8">No ticket data for this period</p>}
        </div>
      )}
    </div>
  );
}
